import type {
  EnvironmentBody,
  EnvironmentBodyKind,
  SiteDetailBodyMyRole,
} from '@/shared/api/generated/model';

/**
 * Окружение в адресе страницы: `/sites/<сайт>/prod/…` и `/sites/<сайт>/dev/…`.
 *
 * В адресе, а не в состоянии вкладки: ссылка коллеге, закладка и вторая
 * вкладка открывают ровно то окружение, которое было на экране, а не
 * «последнее выбранное» (docs/research/04-environments.md).
 */
export type EnvKey = 'prod' | 'dev';

export const ENV_KEYS: readonly EnvKey[] = ['prod', 'dev'];

export const kinds: Record<EnvKey, EnvironmentBodyKind> = {
  prod: 'production',
  dev: 'development',
};

export function isEnvKey(value: string): value is EnvKey {
  return value === 'prod' || value === 'dev';
}

/** Окружение сайта, которое соответствует ключу из адреса. */
export function environmentFor(
  list: readonly EnvironmentBody[],
  key: EnvKey,
): EnvironmentBody | undefined {
  return list.find((item) => item.kind === kinds[key]);
}

export type SiteRole = SiteDetailBodyMyRole;

/**
 * Разрешения — зеркало матрицы сервера (`internal/access/access.go`).
 *
 * Права проверяет сервер в каждой операции. Здесь они нужны, чтобы меню и
 * кнопки не обещали лишнего: недоступное не показывается вовсе, а не висит
 * серым и не отвечает отказом после нажатия (docs/research/07).
 */
export type Permission =
  | 'site.view'
  | 'content.read'
  | 'content.write'
  | 'content.publish'
  | 'site.shape'
  | 'people.manage';

const everything: Permission[] = [
  'site.view',
  'content.read',
  'content.write',
  'content.publish',
  'site.shape',
  'people.manage',
];

const rolePermissions: Record<SiteRole, Permission[]> = {
  super_admin: everything,
  admin: everything,
  developer: ['site.view', 'content.read', 'content.write', 'content.publish', 'site.shape'],
  editor: ['site.view', 'content.read', 'content.write', 'content.publish'],
  author: ['site.view', 'content.read', 'content.write'],
  viewer: ['site.view', 'content.read'],
  support: ['site.view'],
};

/** Даёт ли роль разрешение. Роль ещё не пришла — не даёт ничего. */
export function allows(role: SiteRole | undefined, permission: Permission): boolean {
  return role !== undefined && rolePermissions[role].includes(permission);
}

/**
 * Можно ли менять то, что принадлежит сайту целиком: устройство, адреса,
 * медиатеку. С сужением «только Development» — нельзя: пока это общее для
 * обоих окружений, правка задела бы Production. Так же решает сервер.
 */
export function allowsShared(
  role: SiteRole | undefined,
  devOnly: boolean,
  permission: Permission,
): boolean {
  if (devOnly && permission !== 'site.view' && permission !== 'content.read') {
    return false;
  }

  return allows(role, permission);
}

/** Может ли человек видеть «Модели» и «Настройки». */
export function canShapeSite(role: SiteRole | undefined): boolean {
  return allows(role, 'site.shape');
}
